import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import { DarkModeState } from "../../Jotai/index";
import { me, listUsers, createUser, updateUser, deleteUser, logout } from "../../lib/api";

export default function UsersAdmin() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    (async () => {
      const r = await me();
      if (!r.ok) {
        const redirect = encodeURIComponent(window.location.pathname + window.location.search);
        navigate(`/admin/login?redirect=${redirect}`, { replace: true });
        return;
      }
      const roles = r.user?.roles || [];
      if (!roles.includes("admin")) {
        navigate("/", { replace: true });
        return;
      }
      setIsAdmin(true);
      setAuthChecked(true);
    })();
  }, [navigate]);

  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await listUsers({ q, page: p, pageSize: 20 });
      setItems(res.items || []);
      setPage(res.page || 1);
      setTotalPages(res.totalPages || 1);
      setTotal(res.total || 0);
    } finally { setLoading(false); }
  }, [q]);
  useEffect(() => { if (authChecked) load(1); }, [authChecked, load]);

  const [form, setForm] = useState({ email: "", password: "", name: "", roles: ["editor"], active: true });
  const onCreate = async (e) => {
    e.preventDefault();
    await createUser(form);
    setForm({ email: "", password: "", name: "", roles: ["editor"], active: true });
    await load(page);
  };

  const toggleRole = (id, roles, role) => roles.includes(role) ? roles.filter(r=>r!==role) : roles.concat(role);

  const doLogout = async () => {
    try { await logout(); } catch {}
    navigate("/admin/login", { replace: true });
  };
  const [darkMode] = useAtom(DarkModeState);

  // Delete confirm modal state
  const [confirmUser, setConfirmUser] = useState(null); // { id, email }
  const doDelete = async () => {
    if (!confirmUser) return;
    try {
      await deleteUser(confirmUser.id);
      setConfirmUser(null);
      await load(page);
    } catch (e) {
      alert(e.message || "Failed to delete user");
    }
  };

  if (!authChecked || !isAdmin) return null;
  return (
    <main className={`${darkMode ? "darkmode " : ""}admin-page max-w-5xl mx-auto px-4 py-10`}>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Users</h1>
          <div className="mt-2 text-sm">
            <Link to="/admin" className="underline">&#8592; Back to Admin Dashboard</Link>
          </div>
        </div>
        <button onClick={doLogout} className="px-4 py-2 rounded text-white h-fit" style={{ backgroundColor: '#FF9843' }}>Log out</button>
      </header>

    <section className="mb-8 p-4 border rounded">
        <h2 className="font-medium mb-3">Create user</h2>
        <form onSubmit={onCreate} className="grid gap-3 md:grid-cols-2">
      <input type="email" className="admin-input border rounded px-3 py-2 text-black placeholder-neutral-500" placeholder="Email" required value={form.email} onChange={(e)=>setForm(f=>({...f, email:e.target.value}))} />
      <input type="password" className="admin-input border rounded px-3 py-2 text-black placeholder-neutral-500" placeholder="Temp Password (min 8)" required value={form.password} onChange={(e)=>setForm(f=>({...f, password:e.target.value}))} />
      <input type="text" className="admin-input border rounded px-3 py-2 text-black placeholder-neutral-500" placeholder="Name (optional)" value={form.name} onChange={(e)=>setForm(f=>({...f, name:e.target.value}))} />
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.roles.includes("editor")} onChange={()=>setForm(f=>({...f, roles: toggleRole(null, f.roles, "editor")}))} /> Editor</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.roles.includes("admin")} onChange={()=>setForm(f=>({...f, roles: toggleRole(null, f.roles, "admin")}))} /> Admin</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.active} onChange={()=>setForm(f=>({...f, active: !f.active}))} /> Active</label>
          </div>
          <div className="md:col-span-2">
            <button className="px-4 py-2 rounded text-white" style={{ backgroundColor: '#FF9843' }}>Create</button>
          </div>
        </form>
      </section>

  <section className="mb-6">
        <div className="max-w-md mb-2">
          <form onSubmit={(e)=>{ e.preventDefault(); load(1); }}>
            <div
              className="admin-search flex items-center rounded-lg px-3 py-2.5 shadow-sm focus-within:ring-2 border"
              style={{ '--tw-ring-color': '#4cc8f4' }}
            >
              <svg className="h-4 w-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={q}
                onChange={(e)=>setQ(e.target.value)}
                placeholder="Search users by email or name..."
                className="flex-1 bg-transparent border-0 ring-0 focus:ring-0 outline-none appearance-none leading-5 placeholder-gray-400 text-black"
                style={{ fontSize: window.innerWidth <= 768 ? '16px' : '14px' }}
              />
              {q && (
                <button
                  type="button"
                  onClick={() => { setQ(""); load(1); }}
                  className="ml-2 h-5 w-5 flex items-center justify-center text-gray-400 hover:text-gray-600 outline-none border-0 bg-transparent p-0 focus:outline-none focus:ring-0"
                  aria-label="Clear search"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </form>
        </div>
        {q && (
          <p className="mt-1 mb-3 text-sm text-neutral-300">
            {loading
              ? 'Searching…'
              : (total === 0
                ? 'No users found'
                : `Found ${total} ${total === 1 ? 'user' : 'users'}`)}
          </p>
        )}
        {loading ? <div>Loading…</div> : (
          <div className="overflow-x-auto">
            <table className="min-w-full border text-black">
              <thead><tr className="bg-neutral-50">
                <th className="text-left p-2 border">Email</th>
                <th className="text-left p-2 border">Name</th>
                <th className="text-left p-2 border">Roles</th>
                <th className="text-left p-2 border">Active</th>
                <th className="text-left p-2 border">Actions</th>
              </tr></thead>
        <tbody>
                {items.map(u => (
          <tr key={u._id} className="border-t">
                    <td className="p-2">{u.email}</td>
                    <td className="p-2">{u.name || ""}</td>
                    <td className="p-2">{(u.roles||[]).join(", ")}</td>
                    <td className="p-2">{u.active ? "Yes" : "No"}</td>
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <InlineEdit user={u} onSaved={load} page={page} />
                        <button
                          className="border rounded px-2 py-1 text-red-600 hover:bg-red-50"
                          onClick={() => setConfirmUser({ id: u._id, email: u.email })}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center gap-2 mt-3">
          <button className="border rounded px-3 py-1" disabled={page<=1} onClick={()=>load(page-1)}>Prev</button>
          <span>Page {page} / {totalPages}</span>
          <button className="border rounded px-3 py-1" disabled={page>=totalPages} onClick={()=>load(page+1)}>Next</button>
        </div>
      </section>

      {/* Delete confirm modal */}
      {confirmUser && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50">
          <div className="bg-white rounded-md p-5 w-full max-w-sm">
            <h3 className="font-medium text-lg mb-2">Delete user</h3>
            <p className="text-sm text-neutral-700 mb-4">Are you sure you want to delete “{confirmUser.email}”? This cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <button className="border rounded px-3 py-1" onClick={() => setConfirmUser(null)}>Cancel</button>
              <button className="border rounded px-3 py-1 bg-red-600 text-white" onClick={doDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function InlineEdit({ user, onSaved, page }) {
  const [name, setName] = useState(user.name || "");
  const [roles, setRoles] = useState(user.roles || []);
  const [active, setActive] = useState(!!user.active);
  const [password, setPassword] = useState("");
  const onSave = async () => {
    const patch = { name, roles, active };
    if (password) patch.password = password;
    await updateUser(user._id, patch);
    setPassword("");
    await onSaved(page);
  };
  const toggle = (role) => setRoles(rs => rs.includes(role) ? rs.filter(r=>r!==role) : rs.concat(role));
  return (
    <div className="flex flex-wrap items-center gap-2">
  <input className="admin-input border rounded px-2 py-1 text-black placeholder-neutral-500" value={name} onChange={(e)=>setName(e.target.value)} placeholder="Name" />
      <label className="flex items-center gap-1"><input type="checkbox" checked={roles.includes("editor")} onChange={()=>toggle("editor")} /> Editor</label>
      <label className="flex items-center gap-1"><input type="checkbox" checked={roles.includes("admin")} onChange={()=>toggle("admin")} /> Admin</label>
      <label className="flex items-center gap-1"><input type="checkbox" checked={active} onChange={()=>setActive(a=>!a)} /> Active</label>
  <input type="password" className="admin-input border rounded px-2 py-1 text-black placeholder-neutral-500" value={password} onChange={(e)=>setPassword(e.target.value)} placeholder="New password (optional)" />
      <button className="border rounded px-2 py-1" onClick={onSave}>Save</button>
    </div>
  );
}
